import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  actionTypeStore,
  ActionStatusBarModel,
} from '../store/inspection/observation/actionTypeStore';
import { ObservationModel } from '../data/types';
import { inspectionDB } from '../data/database.structure';

/** Default computation (injectable for tests): empty-safe wrapper over the store. */
async function computeActionStatus(
  observations: ObservationModel[],
): Promise<ActionStatusBarModel> {
  if (!observations || observations.length === 0) {
    const actionType = await inspectionDB.observableActionTypes
      .where('name')
      .equals('NoMeasurements')
      .first(); // Default action type
    return {
      percentage: -1,
      actionType: actionType ?? null,
      maxValue: 100,
      minValue: 0,
      measurement: undefined,
    };
  }
  return actionTypeStore.getActionStatus(observations);
}

/**
 * Optimized action status hook that only recalculates when observations actually change
 * This prevents the constant recalculation that was causing UI lag
 * Handles both empty and non-empty observations to maintain hooks consistency
 */
export function useActionStatusOptimized(
  observations: ObservationModel[],
  compute: (
    observations: ObservationModel[],
  ) => Promise<ActionStatusBarModel> = computeActionStatus,
) {
  // Create a stable key from observations to prevent unnecessary recalculations
  const observationsKey = useMemo(() => {
    if (!observations || observations.length === 0) return 'empty';

    // Create a stable hash from observation values and IDs. The entity MUST be
    // part of the key: unsynced observations share id 0, and two equipment parts
    // can share an observable item (e.g. Shoes and Drive Tumbler both have
    // RollerPath.Wear.Measurement) with DIFFERENT part dimensions - without the
    // entity, the same value on the other part hits this cache and displays the
    // wrong wear percentage.
    return observations
      .map(
        (obs) =>
          `${obs.id}-${obs.entityExternalId}-${obs.dataLevelId}-${obs.value}-${obs.observableItem.id}`,
      )
      .sort()
      .join('|');
  }, [observations]);

  // Always call useQuery to maintain hooks consistency
  const queryResult = useQuery({
    queryKey: ['actionStatus', observationsKey],
    queryFn: () => compute(observations),
    enabled: true, // Always enabled, but queryFn handles empty case
    // Every VALUE change is a NEW query key; without placeholder data the status bar
    // flashed a spinner on each blur (new key -> no data -> isLoading). Keep the
    // previous status visible while the new one computes - the number just updates.
    placeholderData: keepPreviousData,
    // Cache action status for longer since it's expensive to calculate
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    // Only refetch if observations actually changed (not on window focus)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    networkMode: 'always',
  });

  return queryResult;
}
